import type { Page } from '../../types';

const addDetectedError: Page[] = [
  {
    pageType: 'question',
    title: 'Detected error',
    name: 'detected-error',
    description:
      'This section refers to all instances of error that were detected (after the event) in the given quarter. Instances should be reported in the CDR in the quarter it was determined to be error. For example, where an error occurred in quarter 2 but was undetected until quarter 4, this would be included in the Q4 return. All instances of detected error should refer to actual instances and values, not estimates.\n' +
      'Detected error should be reported at the point at which it has been determined fraud, on the civil balance of probabilities, regardless of whether prosecution to the criminal standard is being progressed. This can include instances where fraud is proven to both the civil and criminal standards simultaneously.\n' +
      'Please note: All entries in this section must be attributed to a selection from the typology dropdowns. Please refer to the Typologies Definition document for more detail<br><br>',
    questions: [
      {
        questionType: 'radio',
        title: 'Do you have any instances of detected error to declare?',
        name: 'detected_error_declare',
        options: ['Yes', 'No'],
        rules: {
          required: true,
        },
      },
    ],
  },
  {
    pageType: 'list',
    title: 'Add an instance',
    name: 'detected-error-add-instance',
    type: 'nilreturn',
    fastToggle: true,
    total: {
      columns: [
        { title: 'Number of instances', field: 'detected_error_impact' },
        { title: 'Total Value', field: 'detected_error' },
      ],
    },
    description:
      'This typology refers to the amount of error detected within the given quarter. For reporting purposes, this should not include instances of error that were prevented after the point of detection (these should be reported in Section 2). Entries should include the number of instances and the value of the loss relating to the error.\n' +
      'Error should be reported on the following basis:\n' +
      '<ul><li>where it cannot be determined (upon the civil balance of probabilities) that the instance(s) involved error.</li><li>includes wrong or incorrect payments made to recipients, for example, overpayment to a funding recipient.</li><li>does not include losses arising from wasteful spend, inefficiencies, nugatory spend or ex-gratia payments, for example, late payment fees incurred by the Department/ALB.</li></ul><br/>' +
      'Entries must include selections from the following typology dropdowns:' +
      '<ul><li>the business area in which the error occurred</li><li>the error origin - who was responsible for the mistake (error)?</li><li>whether or not the error related to a COVID-19 scheme or activity</li></ul><br/>',
    disabled: [
      {
        field: 'detected_error_declare',
        condition: 'equals', // optional
        value: 'No',
      },
    ],
    questions: [
      {
        questionType: 'select',
        title: 'Which spending area/business area did the error occur in?',
        name: 'detected_error_area',
        detailsHeading: 'Help with spending areas',
        detailsContent:
          ' <b>Benefits:</b> money given by the government to support members of the public' +
          ' <br/><b>Charges & other non-tax income:</b> money owed to public sector organisations (for example, court fines or prescription fees)' +
          ' <br/><b>Grants:</b> money given by a public sector organisation for a specific purpose' +
          ' <br/><b>Licences & permits:</b> licences, permits or certificates that grant permissions (for example, a Blue Badge, Surveyor licence, marriage certificate)' +
          ' <br/><b>Loans & credit guarantees:</b> money loaned or guaranteed by a public sector organisation' +
          ' <br/><b>Personnel:</b> employees (including contractors and agency workers)' +
          ' <br/><b>Procurement:</b> goods or services bought by public sector organisations (including suppliers)' +
          ' <br/><b>Special payments:</b> money given in unique or specific circumstances (for example, compensation)' +
          ' <br/><b>Subsidies:</b> support or incentives given by the government' +
          ' <br/><b>Tax and duties:</b> mandatory government charge for goods, income or services',
        options: [
          'Benefits',
          'Charges & other non-tax income',
          'Grants',
          'Licences & permits',
          'Loans & credit guarantees',
          'Personnel',
          'Procurement',
          'Special payments',
          'Subsidies',
          'Tax and duties',
        ],
        hintOption: 'Please Select',
        rules: {
          required: true,
        },
      },
      {
        name: 'detected_error_responsible',
        questionType: 'radio',
        title: 'Who was responsible for the error?',
        // hint: 'Select one option',
        detailsHeading: 'Who was responsible for the error?',
        hint: 'who was responsible for the unintentional mistake (error)?',
        detailsContent:
          '<b>Customer:</b> a member of the public or someone acting on behalf of a business or charity' +
          '<br/><b>Official:</b> a government department, public body or service' +
          '<br/><b>Supplier:</b> a person or business contracted to supply a service' +
          '<br/><b>Unknown:</b> I don&apos;t know who was responsible for the mistake?',
        hintOption: 'Please Select',
        options: ['Customer', 'Official', 'Supplier', 'Unknown'],
        rules: {
          required: true,
        },
      },
      {
        questionType: 'radio',
        title: 'Is this related to Covid 19 activity?',
        name: 'detected_error_c19',
        options: ['Yes', 'No'],
        rules: { required: true },
      },
      {
        questionType: 'instanceInput',
        title: 'How many instances occurred?',
        name: 'detected_error_impact',
        txt: `Total number of instances`,
        rules: {
          required: true,
        },
      },
      {
        questionType: 'costInput',
        displaySeperator: 'thousand',
        title: 'What was the value of the error?',
        name: 'detected_error',
        txt: `Total value`,
        rules: {
          required: true,
          decimal: 'decimal(2)',
        },
      },
      {
        questionType: 'textarea',
        title: 'Comments',
        hideOptionalLabel: true,
        name: 'detected_error_comments',
        hint: `Please ensure you do not include any personal or sensitive information`,
        rules: {
          required: false,
        },
      },
    ],
  },
];
export default addDetectedError;
